<script setup>
import { useAnalysisSajuContext, getHarmonyBaseId } from '@/composables/saju-insight/useAnalysisSajuContext';
const context = useAnalysisSajuContext();
let alive = true;
onBeforeUnmount(() => { alive = false; });
const isCurrent = () => alive && (!context || context.isActive());
const filterCurrentLogs = (logs) => (logs || []).filter((log) =>
  !context || getHarmonyBaseId(log) === Number(context.saju.value?.guestUserId));

import { calculateDDay } from '@/utils/dateFormatter';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useDialogManager } from '@/composables/useDialogManager';
import { DEV_MODE } from '@/config/dev-mode';
import { addContentLogAPI } from '@/api/logServiceApi';
import AlertComponent from '@/components/common/Alert.vue';

const analysisStore = useAnalysisStore();
const paymentsStore = usePaymentsStore();
const route = useRoute();
const { alertRef, showAlert, dialogTitle, dialogText, dialogNextUrl } = useDialogManager();

const props = defineProps({
  harmonyTicketData: {
    type: Object,
  },
  harmonyLogData: {
    required: true,
  },
  hasPermission: {
    required: false,
  },
});

const emit = defineEmits(['goPopAnalysis']);

const harmonyLogData = computed(() => {
  if (!props.harmonyLogData?.length) return null;
  return props.harmonyLogData[0];
});

const dDay = computed(() => calculateDDay(harmonyLogData.value?.expiryDate, false));

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const formattedCreatedAt = computed(() => formatDate(harmonyLogData.value?.createdAt));
const formattedExpiryDate = computed(() => formatDate(harmonyLogData.value?.expiryDate));

/** 인원수별 분석권 표시 텍스트 (0건 생략) — 총 N건 / 2인 1건 · 3인 1건 */
const ticketTotalText = computed(() => {
  const details = paymentsStore.harmonyTicketDetails;
  const total = (details.members2.count || 0) + (details.members3.count || 0) + (details.members4.count || 0);
  return `총 ${total}건`;
});

const ticketDetailText = computed(() => {
  const details = paymentsStore.harmonyTicketDetails;
  const parts = [];
  if (details.members2.count > 0) parts.push(`2인 ${details.members2.count}건`);
  if (details.members3.count > 0) parts.push(`3인 ${details.members3.count}건`);
  if (details.members4.count > 0) parts.push(`4인 ${details.members4.count}건`);
  return parts.join(' · ');
});

const goPopAnalysis = () => {
  emit('goPopAnalysis');
};

/** 낭만궁합 시작하기 → 선택 검증 후 detail 페이지로 이동 */
const goStartHarmony = () => {
  const paymentData = analysisStore.harmonyPaymentData;
  const hasCategory = !!analysisStore.harmonySelectedCategory;
  const personCount = analysisStore.harmonyPersonCount;

  // 둘 다 없음
  if (!hasCategory && personCount < 2) {
    showAlert({ title: '관계 유형과 대상자를 선택해주세요.' });
    return;
  }

  // 관계유형만 없음
  if (!hasCategory) {
    showAlert({ title: '관계 유형을 선택해주세요.' });
    return;
  }

  // 카테고리 선택됨 → 무료 조회 로그(카테고리별). 미선택 시 위에서 return 되어 미발화.
  addContentLogAPI({
    contentType: 'ap_harmony_btn_cover_start',
    option3: HARMONY_LABEL_BY_API[analysisStore.harmonySelectedCategory] || analysisStore.harmonySelectedCategory,
  });

  // 대상자만 부족
  if (!paymentData || personCount < 2) {
    showAlert({ title: '대상자를 선택해주세요.', text: '궁합 분석을 위해 최소 2명이 필요합니다.' });
    return;
  }

  // 이미 구매한 궁합이면 안내 팝업 후 이동
  const selectedIds = paymentData.memberGuestUserIds.map(Number);
  const apiCategory = paymentData.category;
  // 카테고리별 캐시(38_LOVER)와 전체 캐시(38) 모두에서 검색
  const allLogs = [
    ...(analysisStore.analysisLogByType[`${ANALYSIS_TYPE_ID}_${apiCategory}`] || []),
    ...(analysisStore.analysisLogByType[ANALYSIS_TYPE_ID] || []),
  ];
  const matched = allLogs.find((log) => {
    if (log.category !== apiCategory) return false;
    const sortedMembers = [...(log.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const logIds = sortedMembers.map((m) => Number(m.guestUserId));
    if (logIds.length !== selectedIds.length) return false;
    return selectedIds.every((id, i) => id === logIds[i]);
  }) || null;

  if (matched) {
    analysisStore.setHarmonyPurchaseState(true, matched);
    showAlert({
      title: '이미 구매하신 궁합입니다.\n구매 데이터로 이동합니다.',
      nextUrl: () => context
        ? context.openHistory('harmony', getHarmonyBaseId(matched), matched.analysisLogId)
        : analysisStore.clickMainMenu('/nangman-harmony'),
    });
    return;
  }

  analysisStore.clickMainMenu('/nangman-harmony');
};

/** 궁합 목록 카드 클릭 → 해당 로그 데이터 store에 세팅 + detail 이동 */
const onClickLogCard = (log) => {
  if (!isCurrent()) return;
  const categoryLabel = HARMONY_CATEGORIES.find(
    (c) => CATEGORY_API_MAP[c.id] === log.category,
  )?.label || log.category || '';
  addContentLogAPI({
    contentType: 'ap_harmony_log_card_open_opt3',
    option3: `${categoryLabel},${log?.analysisLogId ?? ''}`,
  });
  if (context) {
    return context.openHistory('harmony', getHarmonyBaseId(log), log.analysisLogId);
  }
  // harmonyPaymentData도 클릭한 카드의 멤버 정보로 동기화 (matchedPurchaseLog watch의 오매칭 방지)
  const sortedMembers = [...(log.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  analysisStore.setHarmonyPaymentData({
    category: log.category,
    memberGuestUserIds: sortedMembers.map((m) => Number(m.guestUserId)),
    persons: sortedMembers,
  });
  analysisStore.setHarmonyPurchaseState(true, log);
  analysisStore.clickMainMenu('/nangman-harmony');
};

/** 궁합목록 카테고리 */
const HARMONY_CATEGORIES = [
  { id: 'lover', label: '연인', emoji: '💖' },
  { id: 'friend', label: '친구', emoji: '🤝' },
  { id: 'business', label: '비즈니스', emoji: '💼' },
  { id: 'family', label: '가족', emoji: '🏠' },
];

const selectedCategory = ref(null);

/** 카테고리 탭 드래그 스크롤 */
const categoryTabsRef = ref(null);
const isDragging = ref(false);
let dragStartX = 0;
let scrollStartX = 0;

const onTabsMouseDown = (e) => {
  const el = categoryTabsRef.value;
  if (!el) return;
  isDragging.value = false;
  dragStartX = e.pageX;
  scrollStartX = el.scrollLeft;

  const onMouseMove = (ev) => {
    const dx = ev.pageX - dragStartX;
    if (Math.abs(dx) > 3) isDragging.value = true;
    el.scrollLeft = scrollStartX - dx;
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    if (isDragging.value) {
      setTimeout(() => { isDragging.value = false; }, 0);
    }
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};

const onTabsTouchStart = (e) => {
  const el = categoryTabsRef.value;
  if (!el) return;
  isDragging.value = false;
  const touch = e.touches[0];
  dragStartX = touch.pageX;
  scrollStartX = el.scrollLeft;

  const onTouchMove = (ev) => {
    const dx = ev.touches[0].pageX - dragStartX;
    if (Math.abs(dx) > 3) isDragging.value = true;
    el.scrollLeft = scrollStartX - dx;
  };

  const onTouchEnd = () => {
    el.removeEventListener('touchmove', onTouchMove);
    el.removeEventListener('touchend', onTouchEnd);
    if (isDragging.value) {
      setTimeout(() => { isDragging.value = false; }, 0);
    }
  };

  el.addEventListener('touchmove', onTouchMove, { passive: true });
  el.addEventListener('touchend', onTouchEnd);
};

/** 카테고리 키 → API category 값 매핑 */
const CATEGORY_API_MAP = {
  lover: 'LOVER',
  friend: 'FRIEND',
  business: 'BUSINESS',
  family: 'FAMILY',
};

/** API category → 한글 라벨 (로그 option3용) */
const HARMONY_LABEL_BY_API = { LOVER: '연인', FRIEND: '친구', BUSINESS: '비즈니스', FAMILY: '가족' };

const ANALYSIS_TYPE_ID = 38;
const { analysisLogByType } = storeToRefs(analysisStore);
const isCategoryLoading = ref(false);

/** 전체 typeId=38 로그 (PC: props.harmonyLogData에 카테고리 무관 전체가 들어옴) */
const allHarmonyLogs = computed(() => filterCurrentLogs(props.harmonyLogData));

/** 카테고리별 구매 여부 맵 */
const categoryPurchaseMap = computed(() => {
  const map = { lover: false, friend: false, business: false, family: false };
  allHarmonyLogs.value.forEach((log) => {
    const entry = Object.entries(CATEGORY_API_MAP).find(([, api]) => api === log.category);
    if (entry) map[entry[0]] = true;
  });
  return map;
});

/** 전체 카테고리 미구매 여부 */
const hasAnyPurchase = computed(() =>
  Object.values(categoryPurchaseMap.value).some(Boolean),
);

/** 카테고리별 로그 데이터 (props 전체 → 카테고리 필터링, 캐시도 fallback) */
const filteredLogData = computed(() => {
  if (!selectedCategory.value) return [];
  const apiCategory = CATEGORY_API_MAP[selectedCategory.value] || 'LOVER';
  const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCategory}`;
  const cached = analysisLogByType.value[cacheKey];
  if (cached?.length) return filterCurrentLogs(cached);
  return allHarmonyLogs.value.filter((log) => log.category === apiCategory);
});

/** 카테고리 변경 시 해당 카테고리로 API 호출 (미구매 카테고리는 무시) */
const onChangeCategory = async (catId) => {
  if (isDragging.value) return;
  if (!categoryPurchaseMap.value[catId]) return;
  selectedCategory.value = catId;
  const apiCategory = CATEGORY_API_MAP[catId] || 'LOVER';
  const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCategory}`;
  // 캐시에 이미 데이터가 있으면 로딩 표시 불필요
  if (!analysisLogByType.value[cacheKey]) {
    isCategoryLoading.value = true;
  }
  const guestUserId = route.params?.id || route.query?.id || null;
  await analysisStore.getAnalysisLog(guestUserId, ANALYSIS_TYPE_ID, apiCategory);
  isCategoryLoading.value = false;
};

/** 구매 데이터 변경 시 첫 구매 카테고리 자동 선택 (현재 선택이 미구매면 재조정) */
watch(
  categoryPurchaseMap,
  (map) => {
    if (selectedCategory.value && map[selectedCategory.value]) return;
    const firstPurchased = HARMONY_CATEGORIES.find((cat) => map[cat.id]);
    if (firstPurchased) {
      onChangeCategory(firstPurchased.id);
    } else {
      selectedCategory.value = null;
    }
  },
  { immediate: true },
);

/** store의 harmonyPaymentData 참조 */
const { harmonyPaymentData } = storeToRefs(analysisStore);

/**
 * 현재 선택된 카테고리 + 멤버 조합과 매칭되는 결제 로그 찾기
 * - store 캐시에서 카테고리별 로그 참조
 * - 순서 중요: [0]=나, [1~]=대상자 → A,B ≠ B,A
 * - members는 sortOrder 기준 정렬 후 순서대로 비교
 */
const matchedPurchaseLog = computed(() => {
  const paymentData = harmonyPaymentData.value;
  if (!paymentData?.memberGuestUserIds?.length) return null;

  const apiCategory = paymentData.category;
  if (!apiCategory) return null;

  const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCategory}`;
  const categoryLogs = analysisLogByType.value[cacheKey] || [];
  if (!categoryLogs.length) return null;

  const selectedIds = paymentData.memberGuestUserIds.map(Number);

  return categoryLogs.find((log) => {
    const sortedMembers = [...(log.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const logIds = sortedMembers.map((m) => Number(m.guestUserId));
    if (logIds.length !== selectedIds.length) return false;
    return selectedIds.every((id, i) => id === logIds[i]);
  }) || null;
});

// store에 전파 (결제 여부 + 매칭된 로그 데이터)
watch(matchedPurchaseLog, (log) => {
  analysisStore.setHarmonyPurchaseState(!!log, log);
}, { immediate: true });

/** 카테고리 탭 클릭 (유저 액션 전용 — 자동선택은 onChangeCategory 직접 호출) */
const onClickCategoryTab = (catId) => {
  if (!categoryPurchaseMap.value[catId]) return;
  const catLabel = HARMONY_CATEGORIES.find((c) => c.id === catId)?.label || catId;
  addContentLogAPI({ contentType: 'ap_harmony_tab_category_opt3', option3: catLabel });
  onChangeCategory(catId);
};

const formatListDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}.${m}.${d}`;
};
</script>

<template>
  <div class="overview-payment-info-wrap">
    <div class="overview-payment-inner">
      <div class="overview-none-wrap">
        <div class="overview-none-title">
          <i class="ticket-icon__analysis"></i>
          <span class="tit">보유 궁합분석권</span>
          <span class="cnt">
            <span class="cnt__total">{{ ticketTotalText }}</span>
            <template v-if="ticketDetailText">
              <span class="cnt__separator">/</span>
              <span class="cnt__detail">{{ ticketDetailText }}</span>
            </template>
          </span>
        </div>
      </div>

      <div class="overview-pay-info-box harmony-info-box">
        <div class="tit-box"><span>서로의 다름을 이해하는 가장 부드러운 방법</span></div>
        <div class="txt-box">
          가까워질수록 기대와 서운함도 함께 커집니다.<br />
          이제 막 알아가는 관계에도 이해가 필요하죠!<br />
          서로의 스타일과 에너지를 사주 오행의 균형 속에서<br />
          새롭게 조명해 볼 수 있어요.
        </div>
      </div>

      <!-- 궁합목록 -->
      <div class="harmony-list-section">
        <div class="harmony-list-title">구매한 내역</div>
        <div
          ref="categoryTabsRef"
          class="harmony-category-tabs"
          @mousedown="onTabsMouseDown"
          @touchstart="onTabsTouchStart"
        >
          <button
            v-for="cat in HARMONY_CATEGORIES"
            :key="cat.id"
            class="harmony-category-tab"
            :class="{
              'harmony-category-tab--active': selectedCategory === cat.id,
              'harmony-category-tab--disabled': !categoryPurchaseMap[cat.id],
            }"
            :disabled="!categoryPurchaseMap[cat.id]"
            @click="onClickCategoryTab(cat.id)"
          >
            <span class="harmony-category-tab__emoji">{{ cat.emoji }}</span>
            <span class="harmony-category-tab__label">{{ cat.label }}</span>
          </button>
        </div>
        <div class="harmony-list-content" v-if="!isCategoryLoading">
          <template v-if="!hasAnyPurchase || !filteredLogData.length">
            <div class="harmony-list-card">
              <div class="harmony-list-empty">{{ context ? '현재 사주의 구매 내역이 없습니다.' : '구매 내역이 없습니다.' }}</div>
            </div>
          </template>
          <template v-else>
            <div
              v-for="item in filteredLogData"
              :key="item.analysisLogId"
              class="harmony-list-card"
              :class="{ 'harmony-list-card--active': analysisStore.harmonyMatchedLog?.analysisLogId === item.analysisLogId }"
              @click="onClickLogCard(item)"
            >
              <div class="harmony-list-item">
                <div class="harmony-list-item__info">
                  <div class="harmony-list-item__name">
                    {{ [...(item.members || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map((m) => { const n = m.guestUserName || '-'; return n.length > 4 ? n.slice(0, 4) + '…' : n; }).join(' & ') || '궁합 분석' }}
                  </div>
                  <div class="harmony-list-item__dates">
                    <span class="harmony-list-item__date">{{ formatListDate(item.createdAt) }}</span>
                    <span class="harmony-list-item__expiry" v-if="item.expiryDate">
                      유효기간 {{ formatListDate(item.expiryDate) }}
                    </span>
                    <span class="harmony-list-item__expiry" v-else>소장용</span>
                  </div>
                </div>
                <i class="harmony-list-item__arrow"></i>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="overview-btn-box">
      <div class="overview-payment-info-btn" @click="goStartHarmony">
        낭만궁합 시작하기
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AlertComponent
      ref="alertRef"
      :title="dialogTitle"
      :text="dialogText"
      :next-url="dialogNextUrl"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
@import 'src/assets/nangman_style/_analysis_main_color.scss';

.overview-none-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  .overview-none-title {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 20px 28px;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    .tit {
      color: var(--Gray-800, #333);
      font-size: 15px;
      font-weight: 700;
      line-height: 24px;
    }
    .cnt {
      font-size: 14px;
      font-weight: 700;
      line-height: 24px;
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 4px;
      &__total {
        color: var(--orange-300-main, #eb4c10);
      }
      &__separator {
        color: var(--Gray-500, #999);
        font-weight: 500;
      }
      &__detail {
        color: var(--Gray-800, #333);
        font-weight: 500;
      }
    }
  }
}

.overview-payment-info-wrap {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
  width: 100%;
  padding: 28px;
  gap: 24px;
  overflow: hidden;
}

.harmony-info-box {
  background: $analysis-harmony-light;
  gap: 15px;

  .tit-box {
    color: $analysis-harmony;
    font-size: 16px;
    line-height: 26px;
    padding-left: 12px;
  }

  .txt-box {
    background: var(--gray-100-white, #fff);
    border-radius: 12px;
    padding: 16px;
    gap: 0;
    line-height: 1.8;
    width: 100%;
    box-sizing: border-box;
  }
}

.overview-payment-info-content-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 20px;
}

.overview-payment-info-content {
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 20px 28px;

  .overview-payment-info-title {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin-bottom: 8px;
  }

  .overview-payment-info-date-box {
    display: flex;
    width: 100%;
    flex-direction: column;
  }

  .overview-payment-info-date {
    display: flex;
    width: 100%;
    justify-content: space-between;

    .overview-payment-info-date-title {
      color: var(--Gray-800, #333);
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
    }

    .overview-payment-info-date-value {
      color: var(--Gray-800, #333);
      font-size: 14px;
      font-weight: 700;
      line-height: 24px;
      &.red {
        color: var(--orange-300-main, #eb4c10);
      }
    }
  }
}

.overview-pay-info-box {
  display: flex;
  padding: 28px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  border-radius: 12px;
  box-sizing: border-box;
  width: 100%;

  .tit-box {
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .txt-box {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;

    .txt-bold {
      font-weight: 700;
    }
  }
}

.overview-btn-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 28px;
}

.harmony-btn {
  border: 1px solid $analysis-harmony;
  color: $analysis-harmony;
}

.overview-pay-info-btn {
  display: flex;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--gray-100-white, #fff);
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
}

.overview-payment-info-btn {
  cursor: pointer;
  display: flex;
  width: 100%;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
}

.overview-payment-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.ticket-icon__analysis {
  background: url('@manseImages/ticket-icon.svg') center center no-repeat;
  background-size: contain;
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

/* 궁합목록 섹션 */
.harmony-list-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.harmony-list-title {
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.harmony-category-tabs {
  display: flex;
  gap: 10px;
  align-items: center;
  white-space: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:active {
    cursor: grabbing;
  }
}

.harmony-category-tab {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
  padding: 5px 15px;
  border-radius: 100px;
  background: var(--Gray-200, #f8f8f8);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &__emoji {
    font-size: 18px;
    line-height: 25px;
  }

  &__label {
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    color: var(--Gray-600, #757575);
  }

  &--active {
    background: var(--Orange-100, #fff8f5);
    border-color: var(--Orange-200, #ff8800);

    .harmony-category-tab__label {
      color: var(--orange-300-main, #eb4c10);
      font-weight: 700;
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: var(--Gray-200, #f8f8f8);
    }
  }
}

.harmony-list-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.harmony-list-card {
  border: 1px solid var(--Gray-300, #ebebeb);
  border-radius: 10px;
  overflow: hidden;
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: border-color 0.2s ease;

  &--active {
    border-color: var(--Orange-200, #ff8800);
    background: var(--Orange-100, #fff8f5);
  }
}

.harmony-list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  padding: 16px 20px;
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  box-sizing: border-box;
}

.harmony-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  cursor: pointer;

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  &__name {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dates {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    font-size: 13px;
    line-height: 18px;
    width: 100%;
  }

  &__date {
    color: var(--Gray-800, #333);
    font-weight: 400;
  }

  &__expiry {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 500;
  }

  &__arrow {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background: url('@manseImages/icon_info_detail.png') center center no-repeat;
    background-size: 20px auto;
  }
}
</style>
